import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import DoctorDashboard from './pages/DoctorDashboard';
import PatientDashboard from './pages/PatientDashboard';
import ConsultationRoom from './pages/ConsultationRoom';
import StaffDashboard from './pages/StaffDashboard';
import InternalNotificationsBell from './components/InternalNotificationsBell';
import { ROLES, getDashboardForRole, hasPermission, isInternalRole, roleLabels } from './clinical/rbac';
import { getInstitutionalTheme } from './config/institutionalTheme';

const SESSION_TIMEOUT_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];

const AccessDenied = ({ user, message = 'No tienes permiso para acceder a esta seccion.' }) => (
  <div className="access-denied">
    <div className="access-denied-card">
      <span className="access-denied-code">403</span>
      <h1>Acceso restringido</h1>
      <p>{message}</p>
      <p className="access-denied-detail">
        Rol actual: <strong>{roleLabels[user?.role] || 'Sin sesion activa'}</strong>
      </p>
      <a href={getDashboardForRole(user?.role)} className="access-denied-link">
        Volver a mi panel
      </a>
    </div>
  </div>
);

const PortalLanding = ({ theme, user }) => {
  const navigate = useNavigate();
  const activeDashboard = user ? getDashboardForRole(user.role) : '';

  return (
    <main className="portal-landing">
      <section className="portal-landing-shell">
        <div className="portal-landing-brand">
          <div className={`portal-landing-logo ${theme.logoUrl ? 'with-image' : ''}`}>
            {theme.logoUrl ? (
              <img src={theme.logoUrl} alt={theme.institutionName} />
            ) : (
              <span>{theme.logoText}</span>
            )}
          </div>
          <div>
            <p className="portal-landing-kicker">{theme.institutionName}</p>
            <h1>{theme.applicationName}</h1>
            <p>
              
            </p>
          </div>
        </div>

        <div className="portal-choice-grid" aria-label="Seleccion de portal">
          <button
            type="button"
            className="portal-choice-card patient"
            onClick={() => navigate('/portal')}
          >
            <span className="portal-choice-code">PAC</span>
            <strong>Portal paciente</strong>
            <small>Citas, teleconsulta, consentimientos, archivos, recetas e indicaciones.</small>
            <span className="portal-choice-action">Ingresar como paciente</span>
          </button>

          <button
            type="button"
            className="portal-choice-card clinical"
            onClick={() => navigate('/plataforma')}
          >
            <span className="portal-choice-code">MED</span>
            <strong>Portal medico</strong>
            <small>Agenda, admision, pretriaje, consulta, referencias y dashboard.</small>
            <span className="portal-choice-action">Ingresar a plataforma</span>
          </button>
        </div>

        {user && (
          <button
            type="button"
            className="portal-active-session"
            onClick={() => navigate(activeDashboard)}
          >
            Continuar sesion como {user.name}
          </button>
        )}
      </section>
    </main>
  );
};

function AppRoutes() {
  const [theme, setTheme] = useState(() => getInstitutionalTheme());
  const [user, setUser] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionMessage, setSessionMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isConsultationRoute = location.pathname.startsWith('/consultation/');
  const isPortalLoginRoute = location.pathname === '/' || location.pathname === '/plataforma' || location.pathname === '/portal';
  const activeSessionKey = 'telemedActiveUser';
  const internalSessionKey = 'telemedInternalUser';
  const patientSessionKey = 'telemedPatientUser';

  const getSessionKeyForRole = (role) => (
    role === ROLES.patient ? patientSessionKey : internalSessionKey
  );

  const getSessionKeyForPath = (pathname) => {
    if (pathname.startsWith('/plataforma')) return internalSessionKey;
    if (pathname.startsWith('/portal')) return patientSessionKey;
    return activeSessionKey;
  };

  const buildSession = useCallback((userData) => {
    const now = Date.now();
    return {
      user: userData,
      issuedAt: now,
      lastActivityAt: now,
      expiresAt: now + SESSION_TIMEOUT_MS,
    };
  }, []);

  const readSession = useCallback((key) => {
    const rawSession = localStorage.getItem(key);
    if (!rawSession) return null;

    try {
      const parsed = JSON.parse(rawSession);
      if (!parsed?.user) {
        return buildSession(parsed);
      }
      return parsed;
    } catch (error) {
      localStorage.removeItem(key);
      return null;
    }
  }, [buildSession]);

  const writeSession = useCallback((userData, baseSession = null) => {
    const now = Date.now();
    const session = {
      user: userData,
      issuedAt: baseSession?.issuedAt || now,
      lastActivityAt: now,
      expiresAt: now + SESSION_TIMEOUT_MS,
    };
    localStorage.setItem(getSessionKeyForRole(userData.role), JSON.stringify(session));
    localStorage.setItem(activeSessionKey, JSON.stringify(session));
    return session;
  }, []);

  const clearSessions = useCallback(() => {
    localStorage.removeItem(internalSessionKey);
    localStorage.removeItem(patientSessionKey);
    localStorage.removeItem(activeSessionKey);
    localStorage.removeItem('telemedUser');
  }, []);

  const logoutWithMessage = useCallback((message = '') => {
    clearSessions();
    setUser(null);
    setSessionMessage(message);
    navigate('/');
  }, [clearSessions, navigate]);

  const validateStoredSession = useCallback(() => {
    const sessionKey = getSessionKeyForPath(location.pathname);
    const session = readSession(sessionKey) || readSession(activeSessionKey);

    if (!session?.user) {
      if (!isConsultationRoute) setUser(null);
      setSessionReady(true);
      return;
    }

    if (!session.user.role || !roleLabels[session.user.role]) {
      setSessionReady(true);
      logoutWithMessage('Tu sesion no tiene un rol valido. Inicia sesion nuevamente.');
      return;
    }

    if (Date.now() > session.expiresAt) {
      setSessionReady(true);
      logoutWithMessage('Tu sesion expiro por inactividad. Inicia sesion nuevamente.');
      return;
    }

    writeSession(session.user, session);
    setUser(session.user);
    setSessionReady(true);
  }, [isConsultationRoute, location.pathname, logoutWithMessage, readSession, writeSession]);

  useEffect(() => {
    validateStoredSession();
  }, [validateStoredSession]);

  useEffect(() => {
    const syncTheme = (event) => {
      setTheme(event.detail || getInstitutionalTheme());
    };
    window.addEventListener('institutional-theme-updated', syncTheme);
    return () => window.removeEventListener('institutional-theme-updated', syncTheme);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let lastRefresh = 0;
    const refreshActivity = () => {
      const now = Date.now();
      if (now - lastRefresh < 5000) return;
      lastRefresh = now;
      const session = readSession(getSessionKeyForRole(user.role)) || readSession(activeSessionKey);
      if (!session || now > session.expiresAt) {
        logoutWithMessage('Tu sesion expiro por inactividad. Inicia sesion nuevamente.');
        return;
      }
      writeSession(user, session);
    };

    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, refreshActivity, { passive: true }));
    const interval = window.setInterval(() => {
      const session = readSession(getSessionKeyForRole(user.role)) || readSession(activeSessionKey);
      if (!session || Date.now() > session.expiresAt) {
        logoutWithMessage('Tu sesion expiro por inactividad. Inicia sesion nuevamente.');
      }
    }, 30000);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, refreshActivity));
      window.clearInterval(interval);
    };
  }, [logoutWithMessage, readSession, user, writeSession]);

  const handleLogin = (userData) => {
    setUser(userData);
    setSessionMessage('');
    writeSession(userData);
  };

  const handleUpdateUser = useCallback((userData) => {
    setUser(userData);
    writeSession(userData);
  }, [writeSession]);

  const handleLogout = () => {
    logoutWithMessage('Sesion cerrada correctamente.');
  };

  if (!sessionReady) {
    return (
      <div className="app">
        <main className="app-loading">
          <div>
            {theme.logoUrl ? (
              <img src={theme.logoUrl} alt={theme.institutionName} />
            ) : (
              <strong>{theme.logoText}</strong>
            )}
            <span>Preparando sesion...</span>
          </div>
        </main>
      </div>
    );
  }

  return (
      <div className="app">
        {user && !isConsultationRoute && !isPortalLoginRoute && (
          <header className="app-header">
            <div className="header-content">
              <div className={`logo ${theme.logoUrl ? 'with-image' : ''}`}>
                {theme.logoUrl ? (
                  <img className="logo-image" src={theme.logoUrl} alt={theme.institutionName} />
                ) : (
                  <span className="logo-icon">{theme.logoText}</span>
                )}
                <span className="logo-text">
                  {user.role === ROLES.patient ? theme.applicationName : `${theme.applicationName} Interno`}
                </span>
              </div>
              <div className="user-info">
                {isInternalRole(user.role) && <InternalNotificationsBell user={user} />}
                {user.demoMode && <span className="demo-mode-badge">Presentacion</span>}
                <span className="user-name">{user.name}</span>
                <span className="user-role">({roleLabels[user.role] || user.role})</span>
                <button className="logout-btn" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            </div>
          </header>
        )}
        
        <Routes>
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="/" element={<PortalLanding theme={theme} user={user} />} />
          <Route path="/plataforma" element={<Login onLogin={handleLogin} portalType="doctor" sessionMessage={sessionMessage} />} />
          <Route path="/portal" element={<Login onLogin={handleLogin} portalType="patient" sessionMessage={sessionMessage} />} />
          <Route
            path="/plataforma/dashboard"
            element={
              user && isInternalRole(user.role)
                ? (user.role === ROLES.doctor ? <DoctorDashboard user={user} /> : <StaffDashboard user={user} />)
                : user
                  ? <AccessDenied user={user} message="Esta area es solo para personal interno autorizado." />
                  : <Navigate to="/plataforma" />
            }
          />
          <Route
            path="/portal/dashboard"
            element={
              user && user.role === ROLES.patient
                ? <PatientDashboard user={user} updateUser={handleUpdateUser} />
                : user
                  ? <AccessDenied user={user} message="El portal del paciente solo esta disponible para usuarios con rol Paciente." />
                  : <Navigate to="/portal" />
            }
          />
          <Route
            path="/doctor-dashboard"
            element={user && user.role === ROLES.doctor ? <Navigate to="/plataforma/dashboard" /> : <Navigate to="/plataforma" />}
          />
          <Route
            path="/patient-dashboard"
            element={user && user.role === ROLES.patient ? <Navigate to="/portal/dashboard" /> : <Navigate to="/portal" />}
          />
          <Route
            path="/consultation/:appointmentId"
            element={
              user && hasPermission(user, 'consultation.join')
                ? <ConsultationRoom user={user} />
                : user
                  ? <AccessDenied user={user} message="Tu rol no tiene permiso para ingresar a la teleconsulta." />
                  : <Navigate to="/portal" />
            }
          />
        </Routes>

        {!isConsultationRoute && (
          <footer className="app-footer">
            <span>Desarrollado por {theme.developerName}</span>
            <a href={theme.developerUrl} target="_blank" rel="noopener noreferrer">
              {theme.developerDisplayUrl}
            </a>
          </footer>
        )}
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
